import UserService from '@/services/user-service';
import { useAuthStore } from '@/store/use-auth-store';
import { markBackendSessionValidated } from '@/utils/auth-session';
import {
  attemptSoulMapSessionRefresh,
  SOULMAP_SESSION_KEEPALIVE_INTERVAL_MS,
} from '@/utils/soulmap-session-refresh';
import { isAxiosError } from 'axios';
import { useEffect } from 'react';

/**
 * Keeps the LiFT backend session warm during long Soul Map chats by re-establishing
 * the SSO cookie and verifying /auth/me/ before the session expires.
 */
export function useSoulMapSessionKeepalive(enabled = true): void {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authMethod = useAuthStore((state) => state.authMethod);

  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      return;
    }

    let cancelled = false;

    const refreshSession = async (): Promise<void> => {
      if (cancelled) return;

      if (authMethod === 'sso') {
        const refreshed = await attemptSoulMapSessionRefresh();
        if (refreshed) {
          markBackendSessionValidated();
          return;
        }
      }

      try {
        await UserService.getCurrentUser({ skipUnauthorizedRedirect: true });
        markBackendSessionValidated();
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 401) {
          return;
        }

        const recovered = await attemptSoulMapSessionRefresh();
        if (recovered && !cancelled) {
          markBackendSessionValidated();
        }
      }
    };

    void refreshSession();

    const intervalId = window.setInterval(() => {
      void refreshSession();
    }, SOULMAP_SESSION_KEEPALIVE_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [authMethod, enabled, isAuthenticated]);
}
